import React, { useCallback, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { ChevronsUpDownIcon, CirclePlusIcon, FileTextIcon, LayoutGridIcon, LogOutIcon, SettingsIcon, TruckIcon, UsersIcon, XIcon } from 'lucide-react';
import { AkkoBadge, DistrictLogo } from '../BrandMark';
import { useAuth } from '../../contexts/AuthContext';
import { useOutsideClick } from '../../hooks/useOutsideClick';
const primaryNav = [{
  to: '/overview',
  label: 'Overview',
  icon: LayoutGridIcon
}, {
  to: '/claims',
  label: 'Claims',
  icon: FileTextIcon
}, {
  to: '/shipments',
  label: 'Shipments',
  icon: TruckIcon
}, {
  to: '/student-directory',
  label: 'Student Directory',
  icon: UsersIcon
}];
const quickActions = [{
  to: '/claims/create',
  label: 'File New Claim'
}, {
  to: '/shipments/create',
  label: 'Create New Shipment'
}, {
  to: '/devices/create',
  label: 'Add New Device'
}];
const itemBase = 'flex items-center gap-2 rounded-md px-2.5 py-2 text-[12.5px] leading-tight transition-colors';
export function Sidebar() {
  const {
    user,
    signOut
  } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const close = useCallback(() => setMenuOpen(false), []);
  const ref = useOutsideClick<HTMLDivElement>(close);
  return <aside className="fixed inset-y-0 left-0 z-30 flex w-[147px] flex-col bg-navy-900 text-white" data-id="element-85" data-mp="1i9h15qa4y0l2">
      <div className="flex flex-col items-center gap-2.5 px-3 pb-3 pt-3.5" data-id="element-86" data-mp="2ez6ums6s4ccq">
        <DistrictLogo data-id="element-87" data-mp="2a03n913btu1l" />
        <AkkoBadge data-id="element-88" data-mp="29zlu4dbynvxw" />
      </div>

      <nav className="px-2 pt-3" aria-label="Main navigation" data-id="element-89" data-mp="2a0n56hsz8ifm">
        <ul className="space-y-0.5" data-id="element-90" data-mp="25xh1nhx915cu">
          {primaryNav.map(({
          to,
          label,
          icon: Icon
        }) => <li key={to} data-id="element-91" data-mp="20zypzjrukc15">
              <NavLink to={to} className={({
            isActive
          }) => `${itemBase} ${isActive ? 'bg-navy-700 font-medium text-white' : 'text-white/85 hover:bg-navy-800 hover:text-white'}`} data-id="element-92" data-mp="20ynltiuhp29i">
                <Icon className="h-[15px] w-[15px] shrink-0" strokeWidth={1.75} data-id="element-93" data-mp="w00j291fbx8i" />
                <span data-id="element-94" data-mp="vxwfjs7ft08r">{label}</span>
              </NavLink>
            </li>)}
        </ul>
      </nav>

      <p className="px-3.5 pb-1.5 pt-5 text-[11px] text-white/40" data-id="element-95" data-mp="m22u1jbqpweu">Quick Actions</p>
      <ul className="space-y-0.5 px-2" data-id="element-96" data-mp="m3xi36ib64n5">
        {quickActions.map(({
        to,
        label
      }) => <li key={to} data-id="element-97" data-mp="88lavzmyulpc">
            <NavLink to={to} className={({
          isActive
        }) => `${itemBase} ${isActive ? 'bg-navy-700 font-medium text-white' : 'text-white/85 hover:bg-navy-800 hover:text-white'}`} data-id="element-98" data-mp="d1ejiylk2e6p">
              <CirclePlusIcon className="h-[15px] w-[15px] shrink-0" strokeWidth={1.75} data-id="element-99" data-mp="3lgy6jfvmv0db" />
              <span data-id="element-100" data-mp="3qgsvhdvulfvq">{label}</span>
            </NavLink>
          </li>)}
      </ul>

      <div ref={ref} className="relative mt-auto p-2" data-id="element-101" data-mp="36u9bbq8ez9cq">
        {menuOpen && <div className="absolute bottom-full left-2 right-2 mb-1 overflow-hidden rounded-md bg-white py-1 shadow-pop" data-id="element-102" data-mp="31vef9mlfroao">
            <button type="button" onClick={() => {
          setMenuOpen(false);
          navigate('/account-settings');
        }} className="flex w-full items-center gap-2 px-3 py-2 text-[12.5px] text-ink-700 hover:bg-ink-50" data-id="element-103" data-mp="3fvp7ou2e9axy">
              <SettingsIcon className="h-4 w-4 text-ink-500" strokeWidth={1.75} data-id="element-104" data-mp="3kusas8ytd73a" />
              Settings
            </button>
            <button type="button" onClick={() => {
          setMenuOpen(false);
          signOut();
          navigate('/');
        }} className="flex w-full items-center gap-2 px-3 py-2 text-[12.5px] text-ink-700 hover:bg-ink-50" data-id="element-105" data-mp="3chb5ok4jj3g">
              <LogOutIcon className="h-4 w-4 text-ink-500" strokeWidth={1.75} data-id="element-106" data-mp="7h769b6yktnb" />
              Logout
            </button>
          </div>}
        <button type="button" onClick={() => setMenuOpen(v => !v)} aria-expanded={menuOpen} aria-label={`Account menu for ${user?.name ?? ''}`} className="flex w-full items-center gap-2 rounded-md px-1.5 py-2 text-left hover:bg-navy-800" data-id="element-107" data-mp="26ufixzzc9f7o">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/15 text-[11px] font-semibold text-white" data-id="element-108" data-mp="1ix04csojbx3l">
            {user?.initials}
          </span>
          <span className="min-w-0 flex-1" data-id="element-109" data-mp="1rxep9qxl7a6k">
            <span className="block truncate text-[12px] font-bold leading-tight" data-id="element-110" data-mp="1rwn4zc9fwyvl">
              {user?.name.length && user.name.length > 12 ? `${user.name.slice(0, 11)}…` : user?.name}
            </span>
            <span className="block text-[11px] leading-tight text-white/55" data-id="element-111" data-mp="33bs8ujlb53gs">{user?.role}</span>
          </span>
          {menuOpen ? <XIcon className="h-3.5 w-3.5 shrink-0 text-white/60" data-id="element-112" data-mp="38dhnsabni1a5" /> : <ChevronsUpDownIcon className="h-3.5 w-3.5 shrink-0 text-white/60" data-id="element-113" data-mp="3ihpmv0pvzsxh" />}
        </button>
      </div>
    </aside>;
}
export function SidebarSkeleton() {
  return <aside className="fixed inset-y-0 left-0 z-30 flex w-[147px] flex-col bg-navy-900/10 text-white" data-id="element-114" data-mp="38o8ttyxkqhuv">
      <div className="flex flex-col items-center gap-2.5 px-3 pb-3 pt-3.5" data-id="element-115" data-mp="34nybrnwic0zn">
        <span className="h-[58px] w-full animate-pulse rounded-md bg-ink-200" data-id="element-116" data-mp="2zq5a9865fv5p" />
        <span className="opacity-40" data-id="element-117" data-mp="2uphfcbesuu67">
          <AkkoBadge tone="light" data-id="element-118" data-mp="2uofx9mzd97ez" />
        </span>
      </div>
      <div className="px-2 pt-3" data-id="element-119" data-mp="2pq5y71aitmo3">
        <span className={`${itemBase} bg-ink-200/60 text-ink-400`} data-id="element-120" data-mp="2prfx1sp76yvs">
          <LayoutGridIcon className="h-[15px] w-[15px]" strokeWidth={1.75} data-id="element-121" data-mp="2lq48lqi28jp6" />
          Overview
        </span>
      </div>
      <p className="px-3.5 pb-1.5 pt-5 text-[11px] text-ink-300" data-id="element-122" data-mp="ny9l3n8pg4r7">Quick Actions</p>
      <div className="px-2" data-id="element-123" data-mp="jz0u2q51ukzc">
        <span className={`${itemBase} text-ink-400`} data-id="element-124" data-mp="jyr24x0g23r0">
          <CirclePlusIcon className="h-[15px] w-[15px]" strokeWidth={1.75} data-id="element-125" data-mp="2dg6my4sss393" />
          File New Claim
        </span>
      </div>
      <div className="mt-auto flex items-center gap-2 p-3.5" data-id="element-126" data-mp="2i86pm5095a9d">
        <span className="h-7 w-7 animate-pulse rounded-full bg-ink-200" data-id="element-127" data-mp="24gjtc56b81ql" />
        <span className="h-3 flex-1 animate-pulse rounded bg-ink-200" data-id="element-128" data-mp="24e7211enwf0v" />
      </div>
    </aside>;
}